if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        // Set up observer to handle size guide modals immediately when they're inserted
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const sizeGuideModals = node.classList?.contains('size-guide-modal') 
                  ? [node] 
                  : node.querySelectorAll?.('.size-guide-modal') || [];
                
                sizeGuideModals.forEach((modal) => {
                  modal.moved = true; // Prevent connectedCallback from running
                  modal.dataset.section = 'quickadd-modal';
                });
              }
            });
          });
        });

        observer.observe(this.modalContent, { childList: true, subtree: true });

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            // Disconnect observer after HTML is inserted
            observer.disconnect();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            // Initialize size guides in quick-add modal
            this.initializeSizeGuides();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        // Modified: Don't remove size guide modals, only remove other modal-dialog elements
        const modalDialog = productElement.querySelectorAll('modal-dialog:not(.size-guide-modal)');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      initializeSizeGuides() {
        // Wait a bit for DOM to be fully updated
        setTimeout(() => {
          const sizeGuideOpeners = this.modalContent.querySelectorAll('.size-guide-modal__opener');
          const sizeGuideModals = this.modalContent.querySelectorAll('.size-guide-modal');
          
          // Move size guide modals to document body and initialize
          sizeGuideModals.forEach((modal) => {
            if (!modal.parentNode || modal.parentNode !== document.body) {
              document.body.appendChild(modal);
            }
          });
          
          // Set up click handlers for size guide buttons
          sizeGuideOpeners.forEach((opener, index) => {
            const modalId = opener.getAttribute('data-modal');
            const button = opener.querySelector('button');
            
            if (!modalId || !button) {
              return;
            }
            
            // Remove existing handlers to avoid duplicates
            const existingHandler = button._quickAddSizeGuideHandler;
            if (existingHandler) {
              button.removeEventListener('click', existingHandler);
            }
            
            const clickHandler = function(e) {
              e.preventDefault();
              e.stopPropagation();
              
              const targetModal = document.querySelector(modalId);
              if (targetModal) {
                if (typeof targetModal.show === 'function') {
                  targetModal.show(button);
                } else {
                  // Fallback
                  targetModal.setAttribute('open', '');
                  document.body.classList.add('overflow-hidden');
                }
              }
            };
            
            button._quickAddSizeGuideHandler = clickHandler;
            button.addEventListener('click', clickHandler);
          });
        }, 100);
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
